import { abortable } from "../../../api/abortable";

export type ArtworkFailure = Readonly<{ refreshMetadata: boolean; retry: boolean; retryAfterMs: number | null }>;

/** Only called after <img> fails. This public GET never uses the SIWE API client. */
export async function inspectArtworkFailure(url: string, signal: AbortSignal): Promise<ArtworkFailure> {
  const abort = new AbortController();
  const cancel = () => abort.abort(signal.reason);
  signal.addEventListener("abort", cancel, { once: true });
  if (signal.aborted) cancel();
  const timeout = setTimeout(() => abort.abort(), 10_000);
  try {
    const response = await abortable(() => fetch(url, { signal: abort.signal, credentials: "omit", redirect: "error",
      referrerPolicy: "no-referrer" }), abort.signal);
    const retryAfter = response.headers.get("Retry-After");
    const milliseconds = retryAfter === null ? null : /^\d+$/.test(retryAfter) ? Number(retryAfter) * 1000 : Date.parse(retryAfter) - Date.now();
    // Drain/cancel without decoding PNGs on the UI thread or replacing the canonical src with a blob.
    const body = response.body;
    if (body) await abortable(() => body.cancel(), abort.signal);
    return { refreshMetadata: response.status === 409,
      retry: response.ok || [429, 502, 503, 504].includes(response.status),
      retryAfterMs: milliseconds !== null && Number.isFinite(milliseconds) && milliseconds >= 0 ? milliseconds : null };
  } catch {
    signal.throwIfAborted();
    // <img> works cross-origin without CORS; status inspection may be unavailable.
    return { refreshMetadata: false, retry: true, retryAfterMs: null };
  } finally {
    clearTimeout(timeout);
    signal.removeEventListener("abort", cancel);
  }
}
